/**
 * <s-quick-view> — the site-wide quick add / quick view controller (one instance, in the layout).
 *
 * Commands (from product-card buttons, commandfor="quick-view"):
 *   --add   Adds data-variant-id (single-variant products) straight to the cart, then opens the cart drawer.
 *   --open  Fetches the product's quick-view template (data-url + ?view=quick-view) and shows it in the
 *           dialog. The card's selected swatch, when there is one, preselects that variant.
 *
 * Events:
 *   quick-view:add (bubbling, from <s-quick-view-product>) with detail { id, quantity, button }:
 *   adds to cart, closes the dialog and opens the cart drawer.
 *
 * Dataset: data-cart-add-url, data-cart-url (routes), data-label-error.
 */
import { announceGlobalLiveRegion } from '@theme/live-region';
import { openCart, updateCart } from '@theme/cart-actions';

class QuickView extends HTMLElement {
  #ac;
  #cache = new Map();
  #dialog;
  #body;
  #clearTimer;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#body = this.querySelector('[data-quick-view-body]');
    if (!this.#dialog || !this.#body) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (!(event.source instanceof HTMLElement)) return;
      if (event.command === '--open') this.#open(event.source);
      else if (event.command === '--add') this.#quickAdd(event.source);
    }, opts);

    this.addEventListener('quick-view:add', (event) => this.#panelAdd(event), opts);

    // Empty the panel after the close transition so the next product never flashes the previous one.
    this.#dialog.addEventListener('close', () => {
      clearTimeout(this.#clearTimer);
      this.#clearTimer = setTimeout(() => {
        if (!this.#dialog.open) this.#body.replaceChildren();
      }, 350);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#clearTimer);
  }

  #selectedVariant(source) {
    const card = source.closest('[data-product-card]');
    const swatch = card?.querySelector('[data-swatch][aria-pressed="true"]');
    return swatch?.dataset.variantId || source.dataset.variantId || '';
  }

  async #open(source) {
    if (source.getAttribute('aria-busy') === 'true' || !source.dataset.url) return;
    const url = new URL(source.dataset.url, window.location.origin);
    url.searchParams.set('view', 'quick-view');
    const variant = this.#selectedVariant(source);
    if (variant) url.searchParams.set('variant', variant);

    source.setAttribute('aria-busy', 'true');
    try {
      let html = this.#cache.get(url.href);
      if (!html) {
        const response = await fetch(url.href, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(String(response.status));
        html = await response.text();
        this.#cache.set(url.href, html);
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('s-quick-view-product');
      if (!content) throw new Error('missing');
      clearTimeout(this.#clearTimer);
      this.#body.replaceChildren(document.importNode(content, true));
      // Focus the trigger first so <s-dialog> restores focus to it on close.
      source.focus({ preventScroll: true });
      if (!this.#dialog.open) this.#dialog.showModal();
      this.#body.scrollTop = 0;
    } catch {
      window.location.href = source.dataset.url;
    } finally {
      source.removeAttribute('aria-busy');
    }
  }

  async #quickAdd(button) {
    const id = button.dataset.variantId;
    if (!id || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      await this.#addToCart(id, 1);
      button.setAttribute('data-added', '');
      setTimeout(() => button.removeAttribute('data-added'), 1800);
      await openCart();
    } catch (failure) {
      announceGlobalLiveRegion(failure?.message || this.dataset.labelError);
      button.setAttribute('data-error', '');
      setTimeout(() => button.removeAttribute('data-error'), 600);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  async #panelAdd(event) {
    const { id, quantity, button, error } = event.detail || {};
    if (!id || button?.getAttribute('aria-busy') === 'true') return;
    if (error) error.hidden = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      await this.#addToCart(id, quantity || 1);
      this.#dialog.close();
      await openCart();
    } catch (failure) {
      if (error) {
        error.textContent = failure?.message || this.dataset.labelError;
        error.hidden = false;
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  async #addToCart(id, quantity) {
    const result = await updateCart(
      { lines: [{ merchandiseId: id, quantity }] },
      { event: { context: 'product', detail: { source: 's-quick-view' } } }
    );
    const message = result?.userErrors?.[0]?.message;
    if (message) throw new Error(message);
  }
}

if (!customElements.get('s-quick-view')) customElements.define('s-quick-view', QuickView);

